import localforage from 'localforage';

import type {
  RoleSelectionListKey,
  RoleSelectionLists,
  SelectionList,
  SelectionListKey,
  SelectionListState,
} from '../types';

// Memory caches
export const selectionListCache = new Map<
  string,
  SelectionList<SelectionListKey>
>();
export const roleSelectionListCache = new Map<string, RoleSelectionLists>();

// IndexedDB instances
export const selectionListStoreDB = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'rl-dashboard',
  version: 1.0,
  storeName: 'selection-list-store',
});

export const roleSelectionListStoreDB = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'rl-dashboard',
  version: 1.0,
  storeName: 'role-selection-list-store-v2',
});

// Store names — legacy vs reference-merged payloads use separate keys so a
// flag flip does not reuse IndexedDB data that never ran REFERENCE_LISTS_QUERY.
const SELECTION_LIST_STORE_NAME = 'selection-list-store';
const SELECTION_LIST_STORE_REFERENCE_MERGED_KEY =
  'selection-list-store-reference-merged';
const ROLE_SELECTION_LIST_STORE_NAME = 'role-selection-list-store-v2';

const selectionListIndexedDbItemKey = (referenceListsMerged: boolean) =>
  referenceListsMerged
    ? SELECTION_LIST_STORE_REFERENCE_MERGED_KEY
    : SELECTION_LIST_STORE_NAME;

// Generic selection list cache functions
export const getSelectionListsFromStore = async <T extends SelectionListKey>(
  selectionListIds: T[],
  options?: { referenceListsMerged?: boolean }
): Promise<SelectionListState<T> | null> => {
  const referenceListsMerged = options?.referenceListsMerged ?? false;
  const itemKey = selectionListIndexedDbItemKey(referenceListsMerged);
  try {
    const value =
      await selectionListStoreDB.getItem<SelectionListState>(itemKey);
    if (!value) return null;

    // Return cached data only when ALL requested lists are present
    const hasAllLists = selectionListIds.every((id) => value[id] !== undefined);
    return hasAllLists ? (value as SelectionListState<T>) : null;
  } catch (error) {
    console.warn(
      `Error getting selection lists from store: ${selectionListIds.join(', ')}`,
      error
    );
    return null;
  }
};

export const setSelectionListStore = async (
  selectionList: Partial<SelectionListState>,
  options?: { referenceListsMerged?: boolean }
) => {
  const referenceListsMerged = options?.referenceListsMerged ?? false;
  const itemKey = selectionListIndexedDbItemKey(referenceListsMerged);
  try {
    const selectionListStore =
      await selectionListStoreDB.getItem<SelectionListState>(itemKey);

    if (!selectionListStore) {
      await selectionListStoreDB.setItem(itemKey, selectionList);
    } else {
      await selectionListStoreDB.setItem(itemKey, {
        ...selectionListStore,
        ...selectionList,
      });
    }
  } catch (error) {
    console.warn('Error setting selection list store', error);
  }
};

// Role selection list cache functions
export type RoleSelectionListStore = Record<
  string,
  SelectionListState<RoleSelectionListKey>
>;

export const getRoleSelectionListFromStore = async (
  cacheKey: string
): Promise<RoleSelectionLists | null> => {
  try {
    const value =
      await roleSelectionListStoreDB.getItem<RoleSelectionListStore>(
        ROLE_SELECTION_LIST_STORE_NAME
      );
    if (!value) return null;
    return value[cacheKey] ?? null;
  } catch (error) {
    console.warn(`Error getting role selection list store ${cacheKey}`, error);
    return null;
  }
};

export const setRoleSelectionListStore = async (
  selectionList: RoleSelectionListStore
) => {
  try {
    const selectionListStore =
      await roleSelectionListStoreDB.getItem<RoleSelectionListStore>(
        ROLE_SELECTION_LIST_STORE_NAME
      );

    if (!selectionListStore) {
      await roleSelectionListStoreDB.setItem(
        ROLE_SELECTION_LIST_STORE_NAME,
        selectionList
      );
    } else {
      await roleSelectionListStoreDB.setItem(ROLE_SELECTION_LIST_STORE_NAME, {
        ...selectionListStore,
        ...selectionList,
      });
    }
  } catch (error) {
    console.warn('Error setting role selection list store', error);
  }
};

const memorySelectionListKey = (
  id: SelectionListKey,
  referenceListsMerged: boolean
) => `${referenceListsMerged ? 'ref' : 'leg'}:${String(id)}`;

// Memory cache helpers (namespaced by reference merge so flag flips do not
// reuse lists that were never merged from REFERENCE_LISTS_QUERY.)
export const getFromMemoryCache = <T extends SelectionListKey>(
  selectionListIds: T[],
  options?: { referenceListsMerged?: boolean }
): SelectionListState<T> | null => {
  const referenceListsMerged = options?.referenceListsMerged ?? false;
  if (
    selectionListIds.every((id) =>
      selectionListCache.has(memorySelectionListKey(id, referenceListsMerged))
    )
  ) {
    return selectionListIds.reduce(
      (acc, curr) => {
        acc[curr] =
          selectionListCache.get(
            memorySelectionListKey(curr, referenceListsMerged)
          ) || [];
        return acc;
      },
      {} as SelectionListState<T>
    );
  }
  return null;
};

export const updateMemoryCache = (
  data: Partial<SelectionListState>,
  options?: { referenceListsMerged?: boolean }
) => {
  const referenceListsMerged = options?.referenceListsMerged ?? false;
  Object.entries(data).forEach(([key, value]) => {
    if (value !== null) {
      selectionListCache.set(
        memorySelectionListKey(key as SelectionListKey, referenceListsMerged),
        value
      );
    }
  });
};
